import styles from "./PropertyExperts.module.scss";
import cl from "classnames";
import { H2 } from "@/lib/ui/components/Typography";
import { ArrowIcon } from "@/lib/ui/icons";
import { CircleButton } from "@/lib/ui/components/Buttons";
import useEmblaCarousel from "embla-carousel-react";
import ClassNames from "embla-carousel-class-names";
import { useCallback, useEffect, useRef, useState } from "react";
import { EXPERTS } from "../../../../constants/experts";
import { ExpertCard } from "@/lib/ui/components/Cards/ExpertCard";
import { useInterval } from "../../../../hooks/useInterval";
import { useObserver } from "../../../../hooks/useObserver";
import { IExpert } from "../../../../types/experts";
import { LeadType } from "../../../../types/lead-type";
import { eventNames } from "process";
const normalDelay = 5000;
const largeDelay = 7000;
type PropertyExpertsProps = {
  title: string;
  isMobile?: boolean;
  type?: "expert" | "specialist";
  classname?: string;
  experts?: IExpert[];
  leadType?: LeadType;
  modalTitle?: string;
  buttonLabel?: string;
  agreementText?: string;
  eventName?: string[];
};

export const PropertyExperts = ({
  title,
  isMobile,
  type = "expert",
  experts = EXPERTS,
  leadType = 'SALE',
  modalTitle,
  buttonLabel,
  agreementText,
  classname,
  eventName = ["sale"]
}: PropertyExpertsProps) => {
  const isSingleExpert = experts.length === 1;
  const [isPlaying, setIsPlaying] = useState<boolean | undefined>(undefined);
  const [delay, setDelay] = useState<null | number>(normalDelay);
  const [selected, setSelected] = useState(0);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  useObserver({
    ref: sliderRef,
    isInterTrue: () => {
      if (typeof isPlaying === "undefined") {
        setIsPlaying(true);
      }
    },
    isInterFalse: () => {
      if (isPlaying) {
        setIsPlaying(false);
      }
    },
    isOnce: true,
  });
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      duration: 40,
      active: !isSingleExpert,
    },
    [ClassNames()],
  );
  const scrollPrev = useCallback(
    (delay: number | null) => {
      setDelay(delay);
      emblaApi && emblaApi.scrollPrev();
    },
    [emblaApi],
  );
  const scrollNext = useCallback(
    (delay: number | null) => {
      setDelay(delay);
      emblaApi && emblaApi.scrollNext();
    },
    [emblaApi],
  );

  const visibilityChangeHandler = useCallback(() => {
    if (document.visibilityState === "visible") {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", visibilityChangeHandler);
    return () =>
      document.removeEventListener("visibilitychange", visibilityChangeHandler);
  }, [visibilityChangeHandler]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi
        .on("pointerUp", () => {
          setDelay(null);
          setIsPlaying(false);
        })
        .on("select", (e) => setSelected(e.selectedScrollSnap()));
    }
    return () => {
      emblaApi &&
        emblaApi
          .off("pointerUp", () => {
            setDelay(null);
            setIsPlaying(false);
          })
          .off("select", (e) => setSelected(e.selectedScrollSnap()));
    };
  }, [emblaApi]);

  useInterval(() => isPlaying && scrollNext(normalDelay), delay);

  return (
    <div
      className={cl(styles.experts, isMobile && styles["experts--mobile"])}
      ref={sliderRef}
    >
      <div className={styles.container}>
        <div className={styles.experts__top}>
          <H2>{title}</H2>
          {!isSingleExpert && (
            <div
              className={cl(styles.experts__buttons, styles["experts__buttons--desctop"])}
            >
              <CircleButton
                name="scroll to previous"
                as={"button"}
                content={<ArrowIcon />}
                theme={"mixed"}
                onClick={() => {
                  scrollPrev(null);
                  setIsPlaying(false);
                }}
                styleType="secondary"
                className={styles.experts_reviewPrev}
              />
              <CircleButton
                name="scroll to next"
                as={"button"}
                content={<ArrowIcon isRight />}
                theme={"mixed"}
                onClick={() => {
                  scrollNext(null);
                  setIsPlaying(false);
                }}
                styleType="secondary"
                className={styles.experts_reviewNext}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.experts__wrapper} ref={emblaRef}>
        <div
          className={cl(
            styles.experts__slider,
            isSingleExpert ? styles["experts__slider--single"] : "",
          )}
        >
          {experts.map((expert, index) => {
            const onClick = () => {
              emblaApi && emblaApi.scrollTo(index);
            };
            return (
              <ExpertCard
                modalTitle={modalTitle}
                buttonLabel={buttonLabel}
                agreementText={agreementText}
                isSingle={isSingleExpert}
                key={expert.id}
                expert={expert}
                index={index}
                length={experts.length}
                onMouseEnter={() => setDelay(null)}
                onClick={onClick}
                onMouseOut={() => {
                  if (isPlaying) {
                    setDelay(normalDelay);
                  }
                }}
                type={type}
                classname={classname}
                leadType={leadType}
                eventName={eventName}
              />
            );
          })}
        </div>
        {!isSingleExpert && (
          <div
            className={cl(styles.experts__buttons, styles["experts__buttons--mobile"])}
          >
            <CircleButton
              name="scroll to previous"
              as={"button"}
              content={<ArrowIcon />}
              theme={"mixed"}
              onClick={() => scrollPrev(largeDelay)}
              styleType="secondary"
              className={styles.experts_reviewPrev}
            />
            <CircleButton
              name="scroll to next"
              as={"button"}
              content={<ArrowIcon isRight />}
              theme={"mixed"}
              onClick={() => scrollNext(largeDelay)}
              styleType="secondary"
              className={styles.experts_reviewNext}
            />
          </div>
        )}
      </div>
    </div>
  );
};
